import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { RelatedEntityListTab, RelatedEntityTabRecord } from '@/__core/tabs';
import axiosInstance from '@/setup/axios/axiosInstance';

export interface MaterialLine {
  issue_id: string;
  issue_no: string;
  issued_at?: string | null;
  warehouse_name?: string | null;
  item_code?: string;
  item_name?: string;
  uom_name?: string;
  issued_qty?: string | number | null;
  unit_cost?: string | number | null;
  line_amount?: string | number | null;
}

interface MaterialLineRow extends MaterialLine {
  rowId: string;
}

interface MaterialSummaryResponse {
  material_total?: string | number | null;
  lines?: MaterialLine[];
}

interface OrderMaterialsProps {
  orderId?: string;
  salesAmount?: number;
  active?: boolean;
  companyId?: string;
  onOpenIssue?: (record: RelatedEntityTabRecord<MaterialLine>) => void;
}

const toNumber = (value: string | number | null | undefined) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value: string | number | null | undefined) =>
  new Intl.NumberFormat('vi-VN').format(toNumber(value));

const formatDate = (value?: string | null) =>
  value ? new Intl.DateTimeFormat('vi-VN').format(new Date(value)) : '—';

const matchesMaterialSearch = (row: MaterialLineRow, query: string) => [
  row.issue_no,
  row.warehouse_name,
  row.item_code,
  row.item_name,
  row.uom_name,
].some((value) => String(value || '').toLocaleLowerCase('vi-VN').includes(query));

const OrderMaterials: React.FC<OrderMaterialsProps> = ({
  orderId,
  salesAmount = 0,
  active = true,
  companyId,
  onOpenIssue,
}) => {
  const [data, setData] = useState<MaterialSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const refresh = () => setRefreshVersion((current) => current + 1);
    window.addEventListener('inventory:issue-changed', refresh);
    return () => window.removeEventListener('inventory:issue-changed', refresh);
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!active) return () => { isMounted = false; };
    if (!orderId) {
      setData({ material_total: 0, lines: [] });
      return () => { isMounted = false; };
    }

    setLoading(true);
    setError(null);
    axiosInstance
      .get<MaterialSummaryResponse>('/inventory/issues/material-summary/', { params: { order: orderId } })
      .then((response) => {
        if (isMounted) setData(response.data);
      })
      .catch((reason) => {
        if (isMounted) {
          setError(reason?.response?.data?.detail || 'Không thể tải dữ liệu vật tư đã xuất.');
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [orderId, active, refreshVersion]);

  const materialTotal = toNumber(data?.material_total);
  const percent = useMemo(
    () => (salesAmount > 0 ? (materialTotal / salesAmount) * 100 : 0),
    [materialTotal, salesAmount],
  );
  const materialRows = useMemo<MaterialLineRow[]>(
    () => (data?.lines || []).map((line, index) => ({
      ...line,
      rowId: `${line.issue_id}-${line.item_code || 'item'}-${index}`,
    })),
    [data?.lines],
  );

  const materialColumns = useMemo(() => [
    { id: 'issue_no', label: 'Phiếu xuất', width: '140px', render: (_value: unknown, row: MaterialLineRow) => row.issue_no || '—' },
    { id: 'issued_at', label: 'Ngày xuất', width: '130px', render: (_value: unknown, row: MaterialLineRow) => formatDate(row.issued_at) },
    { id: 'warehouse_name', label: 'Kho', width: '180px', render: (_value: unknown, row: MaterialLineRow) => row.warehouse_name || '—' },
    { id: 'item', label: 'Vật tư', width: '280px', render: (_value: unknown, row: MaterialLineRow) => [row.item_code, row.item_name].filter(Boolean).join(' - ') || '—' },
    { id: 'uom_name', label: 'ĐVT', width: '100px', render: (_value: unknown, row: MaterialLineRow) => row.uom_name || '—' },
    { id: 'issued_qty', label: 'Số lượng', width: '130px', align: 'right' as const, render: (_value: unknown, row: MaterialLineRow) => formatCurrency(row.issued_qty) },
    { id: 'unit_cost', label: 'Đơn giá', width: '150px', align: 'right' as const, render: (_value: unknown, row: MaterialLineRow) => formatCurrency(row.unit_cost) },
    { id: 'line_amount', label: 'Thành tiền', width: '160px', align: 'right' as const, render: (_value: unknown, row: MaterialLineRow) => formatCurrency(row.line_amount) },
  ], []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant='outlined' sx={{ width: '100%', maxWidth: '100%', mb: 2, overflowX: 'auto' }}>
        <Table size='small' sx={{ minWidth: 600, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell width='33%'>Tổng tiền vật tư đã xuất</TableCell>
              <TableCell width='33%'>Doanh số đơn hàng</TableCell>
              <TableCell width='34%'>Vật tư / Doanh số</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{formatCurrency(materialTotal)}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{formatCurrency(salesAmount)}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{percent.toFixed(2)}%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>Chi tiết vật tư đã xuất kho</Typography>
      <RelatedEntityListTab
        rows={materialRows}
        columns={materialColumns}
        searchPlaceholder='Tìm kiếm phiếu xuất, kho, mã hoặc tên vật tư...'
        matchesSearch={matchesMaterialSearch}
        emptyTitle='Chưa có vật tư đã xuất kho cho đơn hàng này.'
        getRowId={(row) => row.rowId}
        buildTabRecord={(row) => ({
          id: row.issue_id,
          title: row.issue_no || 'Phiếu xuất kho',
          companyId,
          data: row,
        })}
        onOpenRecord={onOpenIssue}
        resetKey={`${orderId || ''}:${companyId || ''}`}
      />
    </Box>
  );
};

export default OrderMaterials;
